import moment from 'moment/moment';
import { createApi } from '@reduxjs/toolkit/query/react';
import { purchaseRequestApi } from 'api/purchaseRequest';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';

export const supplierOfferApi = createApi({
  tagTypes: ['SUPPLIER_OFFER', 'SUPPLIER_OFFER_LIST', 'SUPPLIER_OFFER_CARDS'],
  reducerPath: 'supplierOfferApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    supplierOfferList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, forDashboard, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        if (forDashboard) {
          params.forDashboard = true;
          params.assignment = JSON.parse(localStorage.getItem('userId') || '');
        }
        return {
          url: `/wrd/api/global-supplier-offer/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        const content = response?.data?.content[0] || {};
        const { managers = {}, responses = [] } = content;

        if (responses.length) {
          content.responses = responses.map((item: any) => ({
            ...item,
            createdByUser: managers[item.createdBy],
            formattedCreatedDate: {
              full: moment(item.createDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment.utc(item.createDate).format('DD.MM.YYYY'),
            },
            formattedValidityDate: item.validity
              ? {
                  full: moment(item.validity).format('DD.MM.YYYY HH:mm:ss'),
                  day: moment.utc(item.validity).format('DD.MM.YYYY'),
                }
              : {},
          }));
        }
        return response.data;
      },
      providesTags: () => ['SUPPLIER_OFFER_LIST'],
    }),
    supplierOfferDelete: builder.mutation<any, string>({
      query: (supplierOfferId) => {
        return {
          url: `/wrd/api/global-supplier-offer/${supplierOfferId}`,
          method: 'delete',
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST']));
        } catch (err) {
          console.warn(err);
        }
      },
      invalidatesTags: () => ['SUPPLIER_OFFER_LIST', 'SUPPLIER_OFFER_CARDS'],
    }),
    supplierOfferPublish: builder.mutation<any, string>({
      query: (supplierOfferId) => ({
        url: `/wrd/api/global-supplier-offer/${supplierOfferId}/publish`,
        method: 'put',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST']));
        } catch (err) {
          console.warn(err);
        }
      },
      invalidatesTags: () => ['SUPPLIER_OFFER_LIST', 'SUPPLIER_OFFER_CARDS', 'SUPPLIER_OFFER'],
    }),
    supplierOfferStar: builder.mutation<any, string>({
      query: (supplierOfferId) => {
        return {
          url: `/wrd/api/global-supplier-offer/star?supplierOfferId=${supplierOfferId}`,
          method: 'put',
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST']));
        } catch (err) {
          console.warn(err);
        }
      },
      invalidatesTags: () => ['SUPPLIER_OFFER_LIST', 'SUPPLIER_OFFER_CARDS'],
    }),
    supplierOfferCardList: builder.query<any, any>({
      query: ({ purchaseId, sort }) => {
        return {
          url: `/wrd/api/global-supplier-offer/cards/list?purchaseId=${purchaseId}&sort=${sort}`,
        };
      },
      transformResponse: (response: any) => {
        return response?.data;
      },
      providesTags: () => ['SUPPLIER_OFFER_CARDS'],
    }),
    supplierOfferItemDelete: builder.mutation<any, string>({
      query: (itemId) => {
        return {
          url: `/wrd/api/global-supplier-offer-items/${itemId}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['SUPPLIER_OFFER'],
    }),
    supplierOfferById: builder.query<any, any>({
      query: ({ supplierOfferId }) => {
        return {
          url: `/wrd/api/global-supplier-offer/${supplierOfferId}`,
        };
      },
      transformResponse: (response: any) => {
        return response?.data;
      },
      providesTags: () => ['SUPPLIER_OFFER'],
    }),
    updateSupplierOfferItem: builder.mutation<any, any>({
      query: ({ data, supplierOfferId }) => ({
        url: `/wrd/api/global-supplier-offer-items?supplierOfferId=${supplierOfferId}`,
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (!arg.attachmentTypes?.length) {
            dispatch(
              supplierOfferApi.util.invalidateTags(['SUPPLIER_OFFER_LIST', 'SUPPLIER_OFFER_CARDS', 'SUPPLIER_OFFER'])
            );
            dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST']));
          }
        } catch (err) {
          console.warn(err);
        }
      },
      invalidatesTags: () => ['SUPPLIER_OFFER'],
    }),
    updateSupplierOffer: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/global-supplier-offer',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (!arg.attachmentTypes?.length) {
            dispatch(
              supplierOfferApi.util.invalidateTags(['SUPPLIER_OFFER_LIST', 'SUPPLIER_OFFER_CARDS', 'SUPPLIER_OFFER'])
            );
            dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST']));
          }
        } catch (err) {
          console.warn(err);
        }
      },
    }),
    createSupplierOffer: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/global-supplier-offer',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (!arg.attachmentTypes?.length) {
            dispatch(supplierOfferApi.util.invalidateTags(['SUPPLIER_OFFER_LIST', 'SUPPLIER_OFFER_CARDS']));
          }
        } catch (err) {
          console.warn(err);
        }
      },
    }),
    supplierOfferItemAlternativeSubstituteDelete: builder.mutation<any, string>({
      query: (itemId) => {
        return {
          url: `/wrd/api/alternative-substitute-items/${itemId}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['SUPPLIER_OFFER'],
    }),
  }),
});
export const {
  useSupplierOfferPublishMutation,
  useSupplierOfferListQuery,
  useCreateSupplierOfferMutation,
  useUpdateSupplierOfferMutation,
  useSupplierOfferByIdQuery,
  useUpdateSupplierOfferItemMutation,
  useSupplierOfferCardListQuery,
  useSupplierOfferItemDeleteMutation,
  useSupplierOfferDeleteMutation,
  useSupplierOfferStarMutation,
  useSupplierOfferItemAlternativeSubstituteDeleteMutation,
} = supplierOfferApi;
